// Single source of truth for classifying an uploaded file by its extension.
//
// Previously each consumer inlined its own `url.split('.').pop()` plus a
// hard-coded extension array. Every copy was case-sensitive, so an iPhone
// export named `IMG_1696.JPEG` matched none of the buckets and rendered as
// nothing at all — the post kept its text and silently dropped its images.
// Normalising here (lowercase, query/hash stripped, URL-decoded) fixes every
// consumer at once and keeps the extension lists from drifting apart.

export const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'jfif',
  'png',
  'gif',
  'webp',
  'heic',
  'heif',
  'bmp',
  'svg',
] as const;

export const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'webm'] as const;
export const PDF_EXTENSIONS = ['pdf'] as const;
export const WORD_EXTENSIONS = ['doc', 'docx'] as const;
export const SPREADSHEET_EXTENSIONS = ['csv', 'xls', 'xlsx'] as const;

export const UNKNOWN_EXTENSION = 'onbekend';

/**
 * Lower-cased extension of a filename or URL, with any query string
 * (e.g. a SAS token) or fragment stripped first. Returns UNKNOWN_EXTENSION
 * when there is no usable extension.
 */
export const getFileExtension = (fileNameOrUrl: string | null | undefined) => {
  if (!fileNameOrUrl) return UNKNOWN_EXTENSION;

  const withoutQuery = fileNameOrUrl.split(/[?#]/)[0];

  let path = withoutQuery;
  try {
    path = decodeURIComponent(withoutQuery);
  } catch {
    // Malformed percent-encoding — fall back to the raw string.
  }

  const lastSegment = path.split('/').pop() ?? '';
  if (!lastSegment.includes('.')) return UNKNOWN_EXTENSION;

  const extension = lastSegment.split('.').pop()?.toLowerCase();
  return extension || UNKNOWN_EXTENSION;
};

// Blob names are minted server-side as `${uuidv4()}_${sanitizedOriginalName}`
// (see server/shared/storage/sas.ts). Callers want the original filename, not
// the full storage URL and not the UUID.
//
// The edit modal previously showed `name: fileUrl` for already-uploaded
// attachments, so the dialog listed raw
// `https://…blob.core.windows.net/uploads/<uuid>_IMG_1696.JPEG` strings while
// newly-added files showed a clean filename — inconsistent and unreadable.
//
// AttachedItems separately derived a name with `split('_').pop()`, which is
// wrong for any original filename containing an underscore: `IMG_1696_-_kopie.JPEG`
// collapsed to `kopie.JPEG`. Strip the UUID prefix explicitly instead, and
// leave legacy blob names (timestamp-style, no UUID) untouched.
const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

export const getDisplayFileName = (fileNameOrUrl: string | null | undefined) => {
  if (!fileNameOrUrl) return '';

  const withoutQuery = fileNameOrUrl.split(/[?#]/)[0];

  let path = withoutQuery;
  try {
    path = decodeURIComponent(withoutQuery);
  } catch {
    // Malformed percent-encoding — fall back to the raw string.
  }

  const lastSegment = path.split('/').pop() ?? '';
  const withoutUuid = lastSegment.replace(UUID_PREFIX, '');
  return withoutUuid || lastSegment;
};

const matches = (
  fileNameOrUrl: string | null | undefined,
  extensions: readonly string[]
) => extensions.includes(getFileExtension(fileNameOrUrl));

export const isImageFile = (fileNameOrUrl: string | null | undefined) =>
  matches(fileNameOrUrl, IMAGE_EXTENSIONS);

export const isVideoFile = (fileNameOrUrl: string | null | undefined) =>
  matches(fileNameOrUrl, VIDEO_EXTENSIONS);

export const isPdfFile = (fileNameOrUrl: string | null | undefined) =>
  matches(fileNameOrUrl, PDF_EXTENSIONS);

export const isWordFile = (fileNameOrUrl: string | null | undefined) =>
  matches(fileNameOrUrl, WORD_EXTENSIONS);

export const isSpreadsheetFile = (fileNameOrUrl: string | null | undefined) =>
  matches(fileNameOrUrl, SPREADSHEET_EXTENSIONS);
